import React, { useEffect, useMemo, useReducer, useState } from 'react';
import SettlePaymentTitle from './SettlePaymentTitle.react';
import SettlePaymentPayNow from './SettlePaymentPayNow.react';
import MaterialDesignDialog from '../../../general/MaterialDesignDialog.react';
import { PAYMENT_TYPE, STEPS } from './const';
import SettlePaymentCreditCard from './SettlePaymentCreditCard.react';
import ReservationConstants from '../../../../constants/ReservationConstants';
import ReservationStore from '../../../../stores/ReservationStore';
import SettlePaymentCreditCardResult from './SettlePaymentCreditCardResult.react';
import { isPendingStatus } from '../../reservation-payment/helpers';
import BookingsActions from '../../../../actions/BookingsActions';
import Analytics from 'arbitrip-common/client/analytics';
import PaymentConstants from '../../../../constants/PaymentConstants';
import SettlePaymentEditCommission from './SettlePaymentEditCommission.react';
import { createDeal } from 'arbitrip-common/client/entities/DealUtils';
import helpers from './helpers';
import PaymentLinkActions from '@/actions/PaymentLinkActions';
import PaymentActions from '@/actions/PaymentActions';
import Stepper from './Stepper';
import { useTranslation } from 'react-i18next';
import SettlePaymentPaymentMethod from './SettlePaymentPaymentMethod.react';
import { NAME_ACTION, nameInitialState, nameReducer } from './reducer/settlePaymentNameReducer';
import SettlePaymentSendPayLaterLink from './SettlePaymentSendPayLaterLink';

const { CURRENCIES } = require('@arbitrip/constants');

const DEFAULT_WINDOW_WIDTH = 842;
const DEFAULT_WINDOW_HEIGHT = 770;

const SettlePaymentMain = ({
	booking,
	profile,
	settlePaymentStatus,
	onKeyDown,
	showSettlePaymentModal,
	setShowSettlePaymentModal,
}) => {
	const { t } = useTranslation();

	const [currentStep, setCurrentStep] = useState(STEPS.PAY_NOW);
	const [stepper, setStepper] = useState(1);
	const [reservationStatus, setReservationStatus] = useState(ReservationStore.getReservationData().status);
	const [chargeCurrency, setChargeCurrency] = useState(profile?.company_currency || CURRENCIES.USD);
	const [method, setMethod] = React.useState(PAYMENT_TYPE.CREDIT_CARD);

	const [nameState, dispatchName] = useReducer(nameReducer, undefined, nameInitialState);

	const payment_link = useMemo(() => {
		return helpers.getPaymentLink(booking);
	}, [booking]);

	useEffect(() => {
		function handleStoreChange() {
			setReservationStatus(ReservationStore.getReservationData().status);
		}

		ReservationStore.addChangeListener(handleStoreChange);

		if (payment_link) {
			// if there is payment link, update the payment link status to success
			PaymentLinkActions.successPaymentLink();
		}

		return () => {
			ReservationStore.removeChangeListener(handleStoreChange);
		};
	}, []);

	useEffect(() => {
		switch (currentStep) {
			case STEPS.PAY_NOW:
			case STEPS.EDIT_COMMISSION:
				setStepper(1);
				break;
			case STEPS.PAYMENT_METHOD:
				setStepper(2);
				break;
			case STEPS.CREDIT_CARD:
			case STEPS.SEND_PAY_LATER_LINK:
				setStepper(3);
				break;
			default:
				setStepper(1);
		}
	}, [currentStep]);

	useEffect(() => {
		if (
			showSettlePaymentModal && // if the model is closed, we don't want to run the logic
			(reservationStatus === ReservationConstants.STATUS.BOOKED ||
				isPendingStatus(reservationStatus) ||
				reservationStatus === ReservationConstants.STATUS.FAILED)
		) {
			setWizardStep(STEPS.CREDIT_CARD_RESULT);
			BookingsActions.updatePaymentMethodToCreditCard(booking.id);

			// set id on deal in order to create a deal entity
			const deal = createDeal({ ...booking.deal, id: booking.id });

			Analytics.actions.interactions.payLaterPaymentCreated(
				profile,
				booking?.hotel,
				deal,
				PaymentConstants.PAYMENT_METHODS_ANALYTICS.PAY_LATER,
				chargeCurrency,
			);
		}
	}, [reservationStatus]);

	const closeDialogAndLock = () => {
		setShowSettlePaymentModal(false);
		PaymentActions.clearPaymentData();
		setMethod(PAYMENT_TYPE.CREDIT_CARD);
		dispatchName({ type: NAME_ACTION.RESET });

		if (payment_link) {
			PaymentLinkActions.successPaymentLink();
		}

		setTimeout(() => {
			setCurrentStep(STEPS.PAY_NOW);
		}, 500);
	};

	const getStepper = () => {
		return (
			<Stepper
				steps={[
					t('Bookings.SettlePayment.PaymentDetails'),
					t('Bookings.SettlePayment.PaymentMethod'),
					t('Bookings.SettlePayment.ConfirmPayment'),
				]}
				current={stepper}
			/>
		);
	};

	const renderStep = () => {
		switch (currentStep) {
			case STEPS.PAY_NOW:
				return {
					title: (
						<SettlePaymentTitle
							title={t('Bookings.SettlePayment.PaymentDetails')}
							subtitle={t('Bookings.SettlePayment.BookingID', { id: booking.short_id })}
							content={getStepper()}
							onClose={closeDialogAndLock}
							booking={booking}
						/>
					),
					content: (
						<SettlePaymentPayNow
							booking={booking}
							profile={profile}
							settlePaymentStatus={settlePaymentStatus}
							onEditCommission={() => setWizardStep(STEPS.EDIT_COMMISSION)}
							nextStep={() => setWizardStep(STEPS.PAYMENT_METHOD)}
							chargeCurrency={chargeCurrency}
							updateChargeCurrency={setChargeCurrency}
							nameState={nameState}
							dispatchName={dispatchName}
						/>
					),
				};

			case STEPS.PAYMENT_METHOD:
				return {
					title: (
						<SettlePaymentTitle
							title={t('Bookings.SettlePayment.PaymentMethod')}
							subtitle={t('Bookings.SettlePayment.ChoosePaymentMethod')}
							content={getStepper()}
							showBackButton={true}
							onBack={() => setWizardStep(STEPS.PAY_NOW)}
							onClose={closeDialogAndLock}
							booking={booking}
						/>
					),
					content: (
						<SettlePaymentPaymentMethod
							profile={profile}
							booking={booking}
							method={method}
							setMethod={setMethod}
							chargeCurrency={chargeCurrency}
							onNext={(method) => {
								if (method === PAYMENT_TYPE.CREDIT_CARD) {
									setWizardStep(STEPS.CREDIT_CARD);
								}

								if (method === PAYMENT_TYPE.SEND_PAY_LATER_LINK) {
									setWizardStep(STEPS.SEND_PAY_LATER_LINK);
								}
							}}
						/>
					),
				};
			case STEPS.CREDIT_CARD:
				return {
					title: (
						<SettlePaymentTitle
							title={t('Bookings.SettlePayment.CreditCard')}
							subtitle={t('Bookings.SettlePayment.CompleteYourPayment')}
							content={getStepper()}
							showBackButton={true}
							onBack={() => setWizardStep(STEPS.PAYMENT_METHOD)}
							onClose={closeDialogAndLock}
							booking={booking}
						/>
					),
					content: (
						<SettlePaymentCreditCard
							profile={profile}
							booking={booking}
							chargeCurrency={chargeCurrency}
							nameState={nameState}
						/>
					),
				};
			case STEPS.CREDIT_CARD_RESULT:
				return {
					title: (
						<SettlePaymentTitle
							title={t('Bookings.SettlePayment.CreditCard')}
							subtitle={
								reservationStatus === ReservationConstants.STATUS.BOOKED ||
								isPendingStatus(reservationStatus)
									? t('Bookings.SettlePayment.PaymentCompleted')
									: t('Bookings.SettlePayment.PaymentFailed')
							}
							onClose={closeDialogAndLock}
							booking={booking}
						/>
					),
					content: (
						<SettlePaymentCreditCardResult
							onClose={closeDialogAndLock}
						/>
					),
				};
			case STEPS.SEND_PAY_LATER_LINK:
				return {
					title: (
						<SettlePaymentTitle
							title={t('Bookings.SettlePayment.SendPaymentLink')}
							subtitle={t('Bookings.SettlePayment.GenerateYourLink')}
							content={getStepper()}
							onClose={closeDialogAndLock}
							showBackButton={true}
							onBack={() => setWizardStep(STEPS.PAYMENT_METHOD)}
							booking={booking}
						/>
					),
					content: (
						<SettlePaymentSendPayLaterLink
							booking={booking}
							profile={profile}
							chargeCurrency={chargeCurrency}
							nameState={nameState}
							onClose={closeDialogAndLock}
							onEdit={() => {
								setWizardStep(STEPS.PAY_NOW);
								PaymentLinkActions.resetPaymentLink();
							}}
						/>
					),
				};
			case STEPS.EDIT_COMMISSION:
				return {
					title: (
						<SettlePaymentTitle
							title={t('Bookings.SettlePayment.EditCommission')}
							subtitle={t('Bookings.SettlePayment.BookingID', { id: booking.short_id })}
							content={getStepper()}
							onClose={closeDialogAndLock}
							booking={booking}
						/>
					),
					content: (
						<SettlePaymentEditCommission
							booking={booking}
							profile={profile}
							chargeCurrency={chargeCurrency}
							onClose={() => setWizardStep(STEPS.PAY_NOW)}
						/>
					),
				};

			default:
				return { title: null, content: null };
		}
	};

	const { title, content } = renderStep();

	const setWizardStep = (step) => {
		setCurrentStep(step);
	};

	return (
		<MaterialDesignDialog
			open={showSettlePaymentModal}
			onClose={() => setShowSettlePaymentModal(false)}
			closeTimeoutInMilliseconds={150}
			key={`settle-payment-modal-${booking.id}`}
			title={title}
			content={content}
			onKeyDown={onKeyDown}
			minWidth={DEFAULT_WINDOW_WIDTH}
			height={DEFAULT_WINDOW_HEIGHT}
			padding={40}
		/>
	);
};

export default SettlePaymentMain;
